import { useState, useEffect, useRef, useCallback } from "react";
import { HERO_INTERVAL_MS, HERO_TRANSITION_MS } from "../config/constants";

/**
 * Manages the hero section's auto-advancing slide state.
 *
 * @param {number} slideCount - Total number of slides.
 * @returns {{
 *   slide: number,
 *   prevSlide: number | null,
 *   animating: boolean,
 *   handleGoTo: (index: number) => void,
 * }}
 */
export function useHeroSlider(slideCount) {
  const [slide, setSlide]         = useState(0);
  const [prevSlide, setPrevSlide] = useState(null);
  const [animating, setAnimating] = useState(false);
  const timerRef      = useRef(null);
  const transitionRef = useRef(null); // tracks the in-flight transition timeout

  // Renamed from startTransition to avoid collision with React 18's startTransition
  const transitionTo = useCallback((from, to) => {
    clearTimeout(transitionRef.current);
    setAnimating(true);
    setPrevSlide(from);
    setSlide(to);
    transitionRef.current = setTimeout(() => {
      setAnimating(false);
      setPrevSlide(null);
    }, HERO_TRANSITION_MS);
  }, []);

  const handleGoTo = useCallback(
    (index) => {
      if (animating || index === slide) return;
      transitionTo(slide, index);
    },
    [animating, slide, transitionTo]
  );

  // Keep a ref to the current slide so the interval callback never goes stale
  const slideRef = useRef(0);
  useEffect(() => { slideRef.current = slide; }, [slide]);

  // Autoplay — capture interval ID locally so cleanup always clears the right one
  useEffect(() => {
    const id = setInterval(() => {
      const current = slideRef.current;
      const next    = (current + 1) % slideCount;
      transitionTo(current, next);
    }, HERO_INTERVAL_MS);
    timerRef.current = id;
    return () => clearInterval(id);
  }, [slideCount, transitionTo]);

  // Clear transition timeout on unmount to prevent setState on unmounted component
  useEffect(() => {
    return () => {
      clearTimeout(transitionRef.current);
      clearInterval(timerRef.current);
    };
  }, []);

  return { slide, prevSlide, animating, handleGoTo };
}
